export type NarrationPlaybackStatus =
  | 'idle'
  | 'loading'
  | 'playing'
  | 'paused'
  | 'ended'
  | 'error';

export type NarrationPlaybackListener = (
  status: NarrationPlaybackStatus,
  playbackToken: number,
) => void;

/**
 * Raw detail behind a failed narration entry, for diagnostics only.
 *
 * `code` is `MediaError.code` verbatim: it distinguishes a decode failure from
 * a network failure, but browsers report an unreachable file and an
 * unsupported container with the same `MEDIA_ERR_SRC_NOT_SUPPORTED`, so it is
 * evidence of a cause, never proof of one.
 */
export interface NarrationMediaFailure {
  source: 'media-error' | 'play-rejected';
  code?: number;
  message: string;
}

export interface NarrationMediaElement {
  src: string;
  currentTime: number;
  playbackRate: number;
  preload: string;
  error?: MediaError | null;
  load: () => void;
  play: () => Promise<void> | void;
  pause: () => void;
  removeAttribute: (name: string) => void;
  addEventListener: (type: string, listener: EventListener) => void;
  removeEventListener: (type: string, listener: EventListener) => void;
}

export interface NarrationMediaAdapter {
  getStatus: () => NarrationPlaybackStatus;
  getSource: () => string | null;
  /** Most recent failure for the current source; cleared on load/restart. */
  getLastError: () => NarrationMediaFailure | null;
  subscribe: (listener: NarrationPlaybackListener) => () => void;
  load: (url: string) => void;
  play: () => Promise<void>;
  pause: () => void;
  restart: (url: string, playbackRate: number, playbackToken?: number) => Promise<void>;
  setPlaybackRate: (playbackRate: number) => void;
  release: () => void;
}

function describeRejection(reason: unknown): string {
  if (reason instanceof Error) {
    return reason.name ? `${reason.name}: ${reason.message}` : reason.message;
  }
  return String(reason);
}

const MEDIA_EVENTS = ['playing', 'waiting', 'pause', 'ended', 'error'] as const;

export function createNarrationMediaAdapter(
  createMediaElement: () => NarrationMediaElement = () => new Audio(),
): NarrationMediaAdapter {
  const media = createMediaElement();
  media.preload = 'auto';

  const listeners = new Set<NarrationPlaybackListener>();
  let status: NarrationPlaybackStatus = 'idle';
  let source: string | null = null;
  let sourceGeneration = 0;
  let playGeneration = 0;
  let activePlaybackToken = 0;
  let sourceHasPlayed = false;
  let lastFailure: NarrationMediaFailure | null = null;
  let attachedListeners: Partial<Record<(typeof MEDIA_EVENTS)[number], EventListener>> = {};

  const emit = (next: NarrationPlaybackStatus, playbackToken = activePlaybackToken): void => {
    if (status === next) return;
    status = next;
    listeners.forEach((listener) => listener(next, playbackToken));
  };

  const detachMediaListeners = (): void => {
    for (const eventName of MEDIA_EVENTS) {
      const listener = attachedListeners[eventName];
      if (listener) media.removeEventListener(eventName, listener);
    }
    attachedListeners = {};
  };

  const attachMediaListeners = (generation: number): void => {
    const guard = (callback: () => void): EventListener => () => {
      if (generation === sourceGeneration) callback();
    };
    attachedListeners = {
      playing: guard(() => {
        sourceHasPlayed = true;
        emit('playing');
      }),
      waiting: guard(() => emit('loading')),
      pause: guard(() => {
        if (status !== 'idle' && status !== 'ended' && status !== 'error') emit('paused');
      }),
      ended: guard(() => {
        if (sourceHasPlayed) emit('ended');
      }),
      error: guard(() => {
        const mediaError = media.error;
        lastFailure = {
          source: 'media-error',
          ...(typeof mediaError?.code === 'number' ? { code: mediaError.code } : {}),
          message: mediaError?.message ?? '',
        };
        emit('error');
      }),
    };
    for (const eventName of MEDIA_EVENTS) {
      const listener = attachedListeners[eventName];
      if (listener) media.addEventListener(eventName, listener);
    }
  };

  const loadWithToken = (url: string, playbackToken: number): void => {
    sourceGeneration += 1;
    playGeneration += 1;
    activePlaybackToken = playbackToken;
    // A stale failure must never be attributed to the incoming entry.
    lastFailure = null;
    detachMediaListeners();
    if (source !== null) media.pause();
    sourceHasPlayed = false;
    source = url;
    media.src = url;
    attachMediaListeners(sourceGeneration);
    emit('loading');
    media.load();
  };

  const load = (url: string): void => {
    loadWithToken(url, activePlaybackToken + 1);
  };

  const play = async (): Promise<void> => {
    if (!source) return;
    const generation = ++playGeneration;
    if (status !== 'playing') emit('loading');
    try {
      await media.play();
    } catch (reason) {
      if (generation === playGeneration && source !== null) {
        lastFailure = { source: 'play-rejected', message: describeRejection(reason) };
        emit('paused');
      }
    }
  };

  const pause = (): void => {
    playGeneration += 1;
    media.pause();
    if (source !== null && status !== 'ended' && status !== 'error') emit('paused');
  };

  const setPlaybackRate = (playbackRate: number): void => {
    media.playbackRate = playbackRate;
  };

  const restart = async (
    url: string,
    playbackRate: number,
    playbackToken = activePlaybackToken + 1,
  ): Promise<void> => {
    // Reload even when replaying the same URL so listeners from the prior
    // playback cannot report completion for the new coordinator step.
    loadWithToken(url, playbackToken);
    playGeneration += 1;
    media.currentTime = 0;
    setPlaybackRate(playbackRate);
    await play();
  };

  const release = (): void => {
    sourceGeneration += 1;
    playGeneration += 1;
    detachMediaListeners();
    media.pause();
    sourceHasPlayed = false;
    source = null;
    media.removeAttribute('src');
    media.load();
    emit('idle');
  };

  return {
    getStatus: () => status,
    getSource: () => source,
    getLastError: () => lastFailure,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    load,
    play,
    pause,
    restart,
    setPlaybackRate,
    release,
  };
}
